<template>
  <Teleport v-if="hasInviteFunctionality" v-bind="teleportInviteProps">
    <InviteListItem @invite="openInviteModal" />
  </Teleport>

  <template
    v-for="(sectionConfig, bundleType, index) in bundlesSectionConfig"
    :key="bundleType"
  >
    <div :class="{extra_store: index > 0 || (hasInviteFunctionality && !teleportInviteProps.to)}">
      <component
        v-for="item in sectionConfig.list"
        :key="item.bundleId"
        :is="sectionConfig.component"
        :item="item"
        :bonus-phase-enabled="bonusPhaseEnabled"
        @select="handleBundleSelect"
      />

      <div
        v-if="hasInviteFunctionality && (bundleTypeEnum.GAMES === bundleType)"
        :id="`invite_${bundleType}`"
      ></div>
    </div>
  </template>

  <BaseLoaderModal v-if="isLoaderModalOpen" />

  <InviteModal
    v-else-if="isInviteModalOpen"
    @invite:resolved="closeInviteModal"
  />

  <BundlePurchase
    v-else-if="isBundlePurchaseVisible"
    :bundle="selectedBundle"
    @purchase:resolved="handleBundlePurchaseResolved"
    @purchase:startNewGame="handleBundlePurchaseStartNewGame"
  />

  <BaseResubscribe
    v-else-if="isResubscribeVisible"
    :show-initial-modal-immediate="true"
    @resubscribe:resolved="handleResubscribeResolved"
  />

  <PreGameSection
    v-else-if="isPreGameVisible"
    @hide:preGame="hidePreGame"
  />
</template>

<script setup>
import { computed, nextTick, ref, watch } from 'vue';
import BaseLoaderModal from '@/components/modal/loader/BaseLoaderModal.vue';
import BaseResubscribe from '@/components/subscription/BaseResubscribe.vue';
import InviteModal from '@/components/modal/InviteModal.vue';
import GameListItem from '@/views/store/components/item/GameListItem.vue';
import JokerListItem from '@/views/store/components/item/JokerListItem.vue';
import InviteListItem from '@/views/store/components/item/InviteListItem.vue';
import BundlePurchase from '@/views/store/components/BundlePurchase.vue';
import PreGameSection from '@/components/section/PreGameSection.vue';
import useAppSettingsStore from '@/store/modules/appSettings.js';
import useAuthStore from '@/store/modules/auth.js';
import useBundleStore from '@/store/modules/bundle.js';
import useModalHook from '@/hooks/useModal.js';
import usePreGameHook from '@/hooks/usePreGame.js';
import { bundleTypeEnum } from '@/constants/bundle.js';

const componentMap = {
  [bundleTypeEnum.GAMES]: GameListItem,
  [bundleTypeEnum.JOKERS]: JokerListItem,
};

const sortMap = {
  [bundleTypeEnum.GAMES]: (a, b) => a.noOfGames - b.noOfGames,
  [bundleTypeEnum.JOKERS]: (a, b) => a.jokers - b.jokers,
};

defineProps({
  bonusPhaseEnabled: {
    type: Boolean,
    default: false,
  },
});

const appSettingsStore = useAppSettingsStore();
const authStore = useAuthStore();
const bundleStore = useBundleStore();

const hasInviteFunctionality = appSettingsStore.appProperties.inviteConfig.inviteEnabled;

const [isLoaderModalOpen, openLoaderModal, closeLoaderModal] = useModalHook();
const [isInviteModalOpen, openInviteModal, closeInviteModal] = useModalHook();
const { isPreGameVisible, launchNewGameFlow, hidePreGame } = usePreGameHook();

const teleportInviteProps = ref({ disabled: true });
const isBundlePurchaseVisible = ref(false);
const isResubscribeVisible = ref(false);
const selectedBundle = ref(null);

const bundlesSectionConfig = computed(() => Object.entries(bundleStore.bundleListPerType)
  .filter(([type, list]) => (list.length > 0) && Object.hasOwn(componentMap, type) && Object.hasOwn(sortMap, type))
  .reduce((acc, [type, list]) => {
    acc[type] = {
      component: componentMap[type],
      list: [...list].sort(sortMap[type]),
    };

    return acc;
  }, {}),
);

const handleBundleSelect = async (bundle) => {
  try {
    openLoaderModal({ hideManually: true });

    await authStore.dispatchRetrieveAuthUser().catch((error) => error);
    await closeLoaderModal({ immediate: true });

    if (authStore.user.subscribed || appSettingsStore.appProperties.isPurchaseBundleFromUnsubUserEnabled) {
      selectedBundle.value = bundle;
      isBundlePurchaseVisible.value = true;
    } else {
      isResubscribeVisible.value = true;
    }
  } catch {
    await closeLoaderModal({ immediate: true });
  }
};

const handleBundlePurchaseResolved = () => {
  isBundlePurchaseVisible.value = false;
  selectedBundle.value = null;
};

const handleResubscribeResolved = () => {
  isResubscribeVisible.value = false;
};

const handleBundlePurchaseStartNewGame = () => {
  handleBundlePurchaseResolved();

  launchNewGameFlow();
};

if (hasInviteFunctionality) {
  watch(bundlesSectionConfig, () => {
    nextTick(() => {
      if (Object.hasOwn(bundlesSectionConfig.value, bundleTypeEnum.GAMES)) {
        teleportInviteProps.value = { to: `#invite_${ bundleTypeEnum.GAMES }` };
      } else {
        teleportInviteProps.value = { disabled: true };
      }
    });
  }, { immediate: true, deep: true });
}
</script>
